/**
 * api/teams.ts —— 队伍（§五 5.4）
 *
 * ⚠ 出参里的 okr / risk / act14 / pax / last / latestSubmittedPeriodNo / prevOwed
 *   【全部由服务端算好下发】，前端只显示。看到前端出现「求平均」「数风险条数」就是红线 4 破了。
 * ⚠ 队伍编号 `code` **不接受传入**：服务端按「含已归档的最大编号 + 1」生成，创建后不可改。
 * ⚠ 归档 / 恢复走独立接口 `PUT /:id/archive`，不要塞进 `updateTeam`——
 *   恢复时有「队名可能已被新队占走」这条独有校验，两个入口都能改必然漏掉它。
 */
import { get, post, put, type ReqOptions } from './client.js';
import type {
  Paged,
  Team,
  TeamAdminView,
  TeamCandidateView,
  TeamCreateInput,
  TeamDetailView,
  TeamRoster,
  TeamSortKey,
  TeamUpdateInput,
  TimelineEntry,
} from '@shared-types';

export interface TeamListQuery {
  /** true = 连已归档一起显示（队伍页的「显示已归档」勾选框） */
  arch?: boolean;
  q?: string;
  sort?: TeamSortKey;
  dir?: 'asc' | 'desc';
}

/** ⚠ 空值不进查询串：`?q=` 这种空参数只会给后端与日志添噪音 */
function qs(q: TeamListQuery): string {
  const p = new URLSearchParams();
  if (q.arch) p.set('arch', '1');
  if (q.q) p.set('q', q.q);
  if (q.sort) p.set('sort', q.sort);
  if (q.dir) p.set('dir', q.dir);
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * 全部队伍（含已归档）—— boot 时 `shared/meta.ts` 缓存的那一份，路由守卫靠它判合法性。
 * 全营 13 支队，一次拉完不分页。
 * ⚠ 【故意含归档队】：队伍管理列表里归档行的「详情」按钮要进得去（需求全文「状态穷举」）。
 * ⚠ 【不要再加一个「只拉在用队」的函数】：两份缓存必然有一天不同步。
 *   要在用队一律 `meta.activeTeams()` 过滤——OKR / 双周报 / 快报都不收归档队。
 */
export const listAllTeams = () => get<Paged<Team>>('/api/teams?arch=1').then((p) => p.items);

export const getTeam = (id: string) => get<Team>(`/api/teams/${encodeURIComponent(id)}`);

/** 队伍管理页的整页数据（§五 5.4 补充 D1）。⚠ 筛选与排序都由服务端做，前端不再排一次 */
export const adminView = (q: TeamListQuery = {}, opts?: ReqOptions) =>
  get<TeamAdminView>(`/api/teams/admin${qs(q)}`, opts);

/** 队伍详情页的整页数据（§五 5.4 补充 D3）。⚠ `current` 为 null 是正常业务态（空当期） */
export const detail = (id: string, opts?: ReqOptions) =>
  get<TeamDetailView>(`/api/teams/${encodeURIComponent(id)}/detail`, opts);

export const members = (id: string, opts?: ReqOptions) =>
  get<TeamRoster>(`/api/teams/${encodeURIComponent(id)}/members`, opts);

export const timeline = (id: string, opts?: ReqOptions) =>
  get<TimelineEntry[]>(`/api/teams/${encodeURIComponent(id)}/timeline`, opts);

/** 成员管理弹窗的取数（§五 5.4 补充 D2，队长 / 管理员）。⚠ 「能不能加」由服务端判定，前端只显示 */
export const candidates = (id: string, opts?: ReqOptions) =>
  get<TeamCandidateView>(`/api/teams/${encodeURIComponent(id)}/candidates`, opts);

export const createTeam = (input: TeamCreateInput) => post<Team>('/api/teams', input);

export const updateTeam = (id: string, input: TeamUpdateInput) =>
  put<Team>(`/api/teams/${encodeURIComponent(id)}`, input);

/** 归档 / 恢复。⚠ 归档【替代删除】：历史 OKR、双周报、打卡全部保留 */
export const setArchived = (id: string, archived: boolean) =>
  put<Team>(`/api/teams/${encodeURIComponent(id)}/archive`, { archived });

/**
 * 管理成员：入参是【全量清单】，不是增量（`volunteerIds` 也是全量）。
 * 🔴 `leadId` 只有管理员能改（§五 5.4 补充 D4）：队长传一个与现值不同的 leadId 会被服务端 403。
 */
export const saveMembers = (
  id: string,
  personIds: string[],
  leadId?: string | null,
  /** 🔴 名单里【以志愿者身份加入】的正式成员（2026-09-14）。在本队只读、不占正式名额 */
  volunteerIds: string[] = [],
) => put<Team>(`/api/teams/${encodeURIComponent(id)}/members`, { personIds, leadId, volunteerIds });
